import { Socket, io } from "socket.io-client";
import utils from "./utils";
import config from "./config";
import { WsListener, WsDataMessage, BetData } from "../types/wsUtils.types";

export class WsUtils {
  url: string = "/listen";
  ws?: Socket;
  private modules: string[] = ["global"];
  private lastModule: string = "";
  private onMeBet?: (bet: BetData) => void;
  private userId?: string;
  private listeners: WsListener = {};
  private initing: boolean = false;
  private token: string = "";
  private nodeKey: string = "";
  private reconnectCount: number = 0;
  private maxReconnectCount: number = 100;
  private isManualClose: boolean = false;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private heartbeatInterval: number = 25000;
  private lastPongTime: number = 0;
  private tokenRefreshTimer?: ReturnType<typeof setInterval>;
  private tokenRefreshInterval: number = 1800000;
  private lastSeqKey = "ws_last_seq";
  private pulling = false;

  private getLastSeq(): number {
    const v = localStorage.getItem(this.lastSeqKey);
    return v ? Number(v) : 0;
  }

  private setLastSeq(seq: number) {
    console.log("setLastSeq", seq);
    if (!seq) return;
    localStorage.setItem(this.lastSeqKey, String(seq));
  }

  constructor() {
    setTimeout(() => {
      this.loopSync();
    }, 1000);

    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleNetworkOnline);
      window.addEventListener("offline", this.handleNetworkOffline);
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  private handleNetworkOnline = async () => {
    if (!this.connected() && !this.isManualClose && this.modules.length > 0) {
      await this.reconnectWithNewToken();
    }
  };

  private handleNetworkOffline = () => {
    this.stopHeartbeat();
  };

  private handleVisibilityChange = async () => {
    if (document.visibilityState !== "visible") return;
    if (this.isManualClose) return;
    if (!this.connected()) {
      if (this.modules.length > 0) {
        await this.reconnectWithNewToken();
      }
    } else {
      const now = Date.now();
      if (now - this.lastPongTime > this.heartbeatInterval * 2) {
        await this.reconnectWithNewToken();
      } else {
        this.startHeartbeat();
      }
    }
  };

  private startHeartbeat = () => {
    this.stopHeartbeat();
    this.lastPongTime = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (!this.ws?.connected) return;
      const now = Date.now();
      if (now - this.lastPongTime > this.heartbeatInterval * 2) {
        this.reconnectWithNewToken();
        return;
      }
      this.ws.emit("ping");
    }, this.heartbeatInterval);
  };

  private stopHeartbeat = () => {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  };

  private reconnectWithNewToken = async () => {
    if (this.initing) return;
    this.closeSocket(false);
    this.initing = true;
    try {
      await this.init();
    } catch (e) {
      console.error("Reconnect failed:", e);
    } finally {
      this.initing = false;
    }
  };

  private startTokenRefresh = () => {
    this.stopTokenRefresh();
    this.tokenRefreshTimer = setInterval(() => {
      if (this.connected() && !this.isManualClose) {
        this.reconnectWithNewToken();
      }
    }, this.tokenRefreshInterval);
  };

  private stopTokenRefresh = () => {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = undefined;
    }
  };

  emit(event: string, ...msg: any) {
    const cbs = this.listeners[event];
    if (!cbs || !cbs.length) {
      return;
    }
    for (const cb of cbs) {
      cb(...msg);
    }
  }

  on(event: string, callback: (...args: any[]) => void) {
    let cbs = this.listeners[event];
    if (!cbs) {
      cbs = this.listeners[event] = [];
    }
    cbs.push(callback);
  }

  removeListener(event: string, callback: (...args: any[]) => void) {
    this.off(event, callback);
  }

  off(event: string, callback: (...args: any[]) => void) {
    let cbs = this.listeners[event];
    if (!cbs) {
      return;
    }
    cbs = cbs.filter((x: any) => x !== callback);
    if (!cbs.length) {
      delete this.listeners[event];
    } else {
      this.listeners[event] = cbs;
    }
  }

  public connected(): boolean {
    return this.ws?.connected || false;
  }

  private loopSync = async () => {
    while (true) {
      await this.sleep(100);
      try {
        await this.syncModules();
      } catch (e) {
        console.error(e);
      }
    }
  };

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private syncModules = async () => {
    if (!this.ws || !this.ws.connected) return;
    let modules: string[] = [...this.modules];
    const user = utils.getUser();
    if (user) {
      try {
        const u = typeof user === "string" ? JSON.parse(user) : user;
        this.userId = u?.userId;
      } catch {
        this.userId = undefined;
      }
    }
    if (this.userId && modules.indexOf(this.userId) < 0) {
      modules.push(this.userId);
    }
    modules = modules.filter(x => x);
    modules.sort();
    const mm = modules.join(",");
    if (this.lastModule === mm) return;
    this.lastModule = mm;
    try {
      const resp = await utils.request("/api/sockmsg/join", modules, {
        alert: false,
      });
      if (resp?.data?.status === 0 && resp.data.model) {
        this.ws.compress(true).emit("join", resp.data.model.token);
      }
    } catch (e) {
      console.error("syncModules failed:", e);
      if (!this.connected()) {
        this.reconnectWithNewToken();
      }
    }
  };

  public open = async (modules: string = "") => {
    const user = utils.getUser();
    if (user) {
      const userInfo = JSON.parse(user);
      this.userId = userInfo.userId;
    }
    const ms = [
      ...new Set(
        modules
          .split(",")
          .map(x => x.trim())
          .filter(x => x)
      ),
    ];
    if (this.userId && !ms.includes(this.userId)) {
      ms.push(this.userId);
    }
    ms.sort();
    this.modules = ms;
    this.autoInit();
  };

  private async autoInit() {
    if (!this.ws && !this.initing) {
      this.initing = true;
      try {
        await this.init();
      } finally {
        this.initing = false;
      }
    }
    if (!this.ws) {
      return;
    }
  }

  onHandleData = (data: WsDataMessage) => {
    const runMessage = () => {
      const self = data.userId === this.userId;
      if (data.type === "balance") {
        this.emit("balance", data.balance);
      } else if (data.type === "bet") {
        if (this.modules && this.modules.indexOf(data.bet.gameId) >= 0) {
          this.emit("bet", data);
        }
        if (data.bet.win) {
          this.emit("bet-win", data);
        }
        if (self) {
          this.emit("me-bet", data);
          if (data.bet.win) {
            this.emit("me-win", data.bet.winAmount);
          }
          if (this.onMeBet) {
            this.onMeBet(data.bet);
          }
        }
      } else if (data.type === "game_data") {
        this.emit("game-data", data);
      } else if (data.type === "bet_begin") {
        this.emit("bet-begin", data);
        if (self) {
          this.emit("me-bet", data);
        } else {
          this.emit("other-bet", data);
        }
      } else if (data.type === "event") {
        this.emit("event", data);
      } else if (data.type === "online") {
        this.emit("online-data", data);
      } else if (data.type === "game_term") {
        this.emit("game-term", data);
      } else {
        this.emit(data.type, data);
      }
    };

    if (data.delaySeconds) {
      setTimeout(runMessage, data.delaySeconds * 1000);
    } else {
      runMessage();
    }
  };

  private init = async () => {
    const resp = await utils.request("/api/sockmsg/init", {}, { alert: false });
    if (resp?.data?.status === 0 && resp.data.model) {
      this.token = resp.data.model.token;
      this.nodeKey = resp.data.model.nodeKey;
    } else {
      throw new Error("Init token failed");
    }

    this.ws = io(config.getWssCdnRootWithoutPath(), {
      path: "/sock.io/",
      transports: ["polling", "websocket", "webtransport"],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectCount,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 15000,
      extraHeaders: {
        Authorization: "Basic " + this.token,
      },
    });

    this.ws.on("data", (data: any) => {
      if (typeof data === "string") {
        if (data[0] !== "{") return;
        if (!data) return;
        data = JSON.parse(data);
      }
      this.lastPongTime = Date.now();
      // 假设服务端给 data.seq
      if (data?.seq) this.setLastSeq(data.seq);

      this.onHandleData(data);
    });

    this.ws.on("connect", async () => {
      this.reconnectCount = 0;
      this.isManualClose = false;
      this.lastPongTime = Date.now();
      this.startHeartbeat();
      this.startTokenRefresh();
      this.emit("ws-connected");
    });

    this.ws.on("disconnect", (reason: string) => {
      this.stopHeartbeat();
      this.stopTokenRefresh();
      this.emit("ws-disconnected", reason);
      if (reason === "io server disconnect" || reason === "transport close") {
        this.reconnectWithNewToken();
      }
    });

    this.ws.on("connect_error", (error: Error) => {
      this.reconnectCount++;
      this.emit("ws-error", error);
      if (this.reconnectCount >= 5) {
        this.reconnectCount = 0;
        this.reconnectWithNewToken();
      }
    });

    this.ws.on("reconnect_failed", () => {
      this.emit("ws-reconnect-failed");
      this.reconnectWithNewToken();
    });

    this.ws.on("reconnect", () => {
      this.reconnectCount = 0;
    });

    this.ws.on("pong", () => {
      this.lastPongTime = Date.now();
    });
  };

  private closeSocket(manual: boolean = true) {
    if (manual) this.isManualClose = true;
    this.stopHeartbeat();
    this.stopTokenRefresh();
    if (this.ws) {
      this.ws.removeAllListeners();
      this.ws.close();
      this.ws = undefined;
    }
  }

  /**
   * 域名容灾软恢复：确保连接已建立。
   *
   * 首次 open 时如果 /api/sockmsg/init 失败（主域名被封时的首波请求就是这样），
   * init() 会在创建 socket 之前抛出 "Init token failed"，this.ws 压根没被赋值——
   * 而 socket.io 那套自动重连（reconnection / connect_error / reconnect_failed）
   * 全都建立在 socket 对象存在的前提上，于是连接会永久缺失、整个会话收不到实时消息。
   * 代理切到备用域名后调用本方法，重新走一遍 init。
   *
   * 幂等：已连接时 autoInit 里的 `!this.ws` 判断直接跳过，不会产生第二条连接；
   * 也不碰 this.modules，useWsModules 注册过的订阅不受影响。
   */
  public ensureConnected = async () => {
    await this.autoInit();
  };

  public close() {
    this.closeSocket(true);
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleNetworkOnline);
      window.removeEventListener("offline", this.handleNetworkOffline);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  public reset() {
    this.isManualClose = false;
    this.lastModule = "";
    this.userId = undefined;
    this.modules = ["global"];
    this.token = "";
    this.nodeKey = "";
    this.reconnectCount = 0;
    this.initing = false;
    this.closeSocket(false);
  }

  async finishWin(record: any) {
    await utils.request("/api/game/finish_hold", { id: record.id });
    if (record.winAmount <= 0) return;
    this.emit("me-win", record.winAmount);
    this.emit("balance");
  }

  async checkBetResult(id: string): Promise<any> {
    return new Promise<any>(resolve => {
      let hasReturn = false;
      const callback = (resp: any) => {
        if (hasReturn) return;
        hasReturn = true;
        resolve(resp);
        this.onMeBet = undefined;
      };
      this.onMeBet = (bet: any) => {
        if (bet.id !== id) return;
        callback({ status: 0, model: bet });
      };

      const pollResult = async () => {
        while (!hasReturn) {
          const resp = await utils.request("/api/game/bet_result", { id: id });
          if (resp.data.status === 0) {
            if (resp.data.model.result !== "PENDING") {
              callback(resp.data);
              break;
            }
          }
          await this.sleep(1000);
        }
      };
      pollResult();
    });
  }
}

const _wsUtils = new WsUtils();

export const wsUtils = _wsUtils;
